/**
 * iter75.2 — Fuel Receipts (Control Room).
 *
 * Drivers upload photos of their fuel receipts + type in the total.
 * Admin lists, previews, approves, or rejects. Approved receipts
 * feed the "Fuel refund" line on the sheet-driven payout PDF
 * (own-vehicle drivers only).
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { ReceiptVerifyModal } from "../ReceiptVerifyModal";
import { MonthlyRefundsPanel } from "../MonthlyRefundsPanel";
import { BulkQueuePanel } from "../BulkQueuePanel";
import {
  Check, X, Trash2, Image as ImageIcon, ArrowUpRight, ChevronDown,
  ChevronRight, Filter, FileText, FileSpreadsheet, Mail, Send, Settings2,
} from "lucide-react";

const eur = (n) => {
  if (n === null || n === undefined || n === "") return "—";
  return `€${Number(n).toFixed(2)}`;
};

const STATUS_CHIPS = [
  { key: "", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
];

const CATEGORY_OPTIONS = [
  { slug: "gorivo",   label: "Gorivo",              emoji: "⛽" },
  { slug: "dijelovi", label: "Dijelovi za vozilo",  emoji: "🔩" },
  { slug: "servis",   label: "Servis vozila",       emoji: "🛠️" },
  { slug: "gume",     label: "Gume",                emoji: "🛞" },
  { slug: "parking",  label: "Parking i cestarine", emoji: "🅿️" },
  { slug: "pranje",   label: "Pranje i održavanje", emoji: "🧼" },
];

export function FuelReceiptsTab() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [driverFilter, setDriverFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);       // { id, driver_name, ... }
  const [previewUrl, setPreviewUrl] = useState(null); // object URL for image
  const [totals, setTotals] = useState({ amount: 0, refund: 0 });
  const [showEmail, setShowEmail] = useState(false);
  const [accEmail, setAccEmail] = useState(() => localStorage.getItem("juke_accountant_email") || "");
  const [sending, setSending] = useState(false);
  const [selected, setSelected] = useState([]);       // receipt ids for bulk actions
  const [bulkName, setBulkName] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [driverNames, setDriverNames] = useState([]);

  const [driversList, setDriversList] = useState([]);

  useEffect(() => {
    api.get("/admin/drivers")
      .then(({ data }) => {
        setDriverNames([...new Set((data || []).map((d) => d.name).filter(Boolean))].sort());
        setDriversList((data || []).filter((d) => d.name).sort((a, b) => a.name.localeCompare(b.name)));
      })
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (status) params.status = status;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await api.get("/admin/fuel-receipts", { params });
      let list = data?.rows || [];
      if (driverFilter.trim()) {
        const q = driverFilter.trim().toLowerCase();
        list = list.filter((r) => (r.driver_name || "").toLowerCase().includes(q));
      }
      setRows(list);
      setSelected((sel) => sel.filter((id) => list.some((r) => r.id === id)));
      setTotals({
        amount: data?.total_amount_eur || 0,
        refund: data?.total_refund_eur || 0,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load fuel receipts.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [status, driverFilter, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const toggleSelect = (id) =>
    setSelected((sel) => (sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]));

  const toggleSelectAll = () =>
    setSelected((sel) => (sel.length === rows.length ? [] : rows.map((r) => r.id)));

  const bulkAssign = async (name) => {
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/fuel-receipts/bulk-accounting-name", {
        ids: selected, accounting_name: name,
      });
      toast.success(name
        ? `Ime za knjigovodstvo "${name}" postavljeno na ${data.updated} računa.`
        : `Vraćeno na originalnog vozača za ${data.updated} računa.`);
      setSelected([]);
      setBulkName("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Grupna promjena nije uspjela.");
    } finally {
      setBulkBusy(false);
    }
  };

  const setReceiptStatus = async (id, s) => {
    try {
      await api.patch(`/admin/fuel-receipts/${id}`, { status: s });
      toast.success(`Marked ${s}.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update.");
    }
  };

  const deleteReceipt = async (id) => {
    if (!window.confirm("Delete this receipt?")) return;
    try {
      await api.delete(`/admin/fuel-receipts/${id}`);
      toast.success("Deleted.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    }
  };

  const openPreview = async (row) => {
    setPreview(row);
    try {
      const resp = await api.get(`/admin/fuel-receipts/${row.id}/photo`, {
        responseType: "blob",
      });
      setPreviewUrl(URL.createObjectURL(resp.data));
    } catch {
      setPreviewUrl(null);
      toast.error("Could not load photo.");
    }
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreview(null);
  };

  const dateQuery = () =>
    (dateFrom ? `&date_from=${dateFrom}` : "") + (dateTo ? `&date_to=${dateTo}` : "");

  const [pdfBusy, setPdfBusy] = useState(false);
  // PDF is generated 100% in the browser (jsPDF) — the backend only serves
  // tiny JSON + one small thumbnail at a time, so server OOM/520 is impossible.
  const exportPdf = async () => {
    if (!rows.length) return toast.error("Nema računa za izvoz — provjerite filtre.");
    setPdfBusy(true);
    const tid = "export-pdf";
    try {
      toast.loading("Priprema PDF izvještaja…", { id: tid });
      const { buildReceiptReportPdf } = await import("@/lib/receiptReportPdf");
      const fetchPhotoBlob = async (id, page = 0) => {
        const resp = await api.get(`/admin/fuel-receipts/${id}/thumb?page=${page}`, {
          responseType: "blob", timeout: 30000,
        });
        return resp.data;
      };
      const { blob, failed } = await buildReceiptReportPdf({
        rows, dateFrom, dateTo, fetchPhotoBlob,
        onProgress: (i, n) =>
          toast.loading(`Priprema PDF — slika ${i}/${n}…`, { id: tid }),
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `izvjestaj-racuni-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(link.href), 30_000);
      toast.success(
        failed > 0
          ? `PDF preuzet — ${rows.length} računa (${failed} slika nedostupno).`
          : `PDF preuzet — svih ${rows.length} računa.`,
        { id: tid, duration: 7000 },
      );
    } catch (e) {
      toast.error(e?.message || "Izvoz nije uspio — pokušajte ponovno.", { id: tid });
    } finally {
      setPdfBusy(false);
    }
  };

  const downloadXlsx = async () => {
    try {
      toast.loading("Priprema Excel datoteke…", { id: "export-xlsx" });
      const token = localStorage.getItem("juke_admin_token");
      const base = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(
        `${base}/api/admin/fuel-receipts/export.xlsx?status=approved${dateQuery()}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err?.detail || `HTTP ${resp.status}`);
      }
      const blob = await resp.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "manifest.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(link.href), 30_000);
      toast.success("Excel preuzet.", { id: "export-xlsx" });
    } catch (e) {
      toast.error(e.message || "Izvoz nije uspio", { id: "export-xlsx" });
    }
  };

  const changeCategory = async (id, category) => {
    try {
      await api.patch(`/admin/fuel-receipts/${id}`, { category });
      toast.success("Kategorija promijenjena — povrat ponovno izračunat.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Promjena kategorije nije uspjela.");
    }
  };

  const sendToAccountant = async () => {
    const email = accEmail.trim();
    if (!/^\S+@\S+\.\S+$/.test(email)) return toast.error("Unesite ispravnu e-mail adresu.");
    setSending(true);
    try {
      const { data } = await api.post("/admin/fuel-receipts/email-accountant", {
        email, status: "approved",
        ...(dateFrom ? { date_from: dateFrom } : {}),
        ...(dateTo ? { date_to: dateTo } : {}),
      });
      localStorage.setItem("juke_accountant_email", email);
      toast.success(data.sent
        ? `Poslano na ${email} (${data.receipts} računa).`
        : "Excel pripremljen, ali e-mail nije poslan (RESEND ključ nije postavljen).");
      setShowEmail(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Slanje nije uspjelo.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="fuel-receipts-tab">
      {/* Filters + totals */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="inline-flex items-center gap-1 bg-black/40 border border-white/10 rounded-full p-1">
          {STATUS_CHIPS.map((c) => (
            <button
              key={c.key || "all"}
              onClick={() => setStatus(c.key)}
              className={`px-3 py-1 rounded-full text-xs transition ${
                status === c.key
                  ? "bg-[#C5A059] text-black"
                  : "text-zinc-400 hover:text-white"
              }`}
              data-testid={`fuel-filter-${c.key || "all"}`}
            >
              {c.label}
            </button>
          ))}
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
          <input
            value={driverFilter}
            onChange={(e) => setDriverFilter(e.target.value)}
            placeholder="Filter by driver…"
            className="bg-black/40 border border-white/10 pl-8 pr-3 py-1.5 rounded-lg text-sm focus:outline-none focus:border-[#C5A059]/60 w-52"
            data-testid="fuel-driver-filter"
          />
        </div>
        <div className="flex items-center gap-1.5 bg-black/40 border border-white/10 rounded-lg px-2 py-1">
          <span className="text-[10px] uppercase tracking-widest text-zinc-500">Od</span>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="bg-transparent text-sm text-zinc-200 focus:outline-none [color-scheme:dark]"
            data-testid="fuel-date-from"
          />
          <span className="text-[10px] uppercase tracking-widest text-zinc-500">Do</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="bg-transparent text-sm text-zinc-200 focus:outline-none [color-scheme:dark]"
            data-testid="fuel-date-to"
          />
          {(dateFrom || dateTo) && (
            <button
              onClick={() => { setDateFrom(""); setDateTo(""); }}
              className="text-zinc-500 hover:text-white transition"
              title="Očisti raspon datuma"
              data-testid="fuel-date-clear"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <div className="ml-auto flex items-center gap-6 text-xs">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Total submitted</div>
            <div className="text-zinc-200 font-semibold">{eur(totals.amount)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Approved refund</div>
            <div className="text-[#C5A059] font-semibold">{eur(totals.refund)}</div>
          </div>
          <button
            onClick={downloadXlsx}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#C5A059] text-black text-sm font-semibold hover:bg-[#D4AF37] transition"
            title="Preuzmi styled Excel manifest (Croatian headers + poveznice na slike)"
            data-testid="fuel-export-xlsx"
          >
            <FileSpreadsheet className="w-4 h-4" /> Preuzmi Excel za knjigovođu
          </button>
          <div className="relative">
            <button
              onClick={() => setShowEmail((v) => !v)}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#C5A059]/40 text-[#C5A059] text-sm hover:bg-[#C5A059]/10 transition"
              data-testid="fuel-email-accountant-open"
            >
              <Mail className="w-4 h-4" /> Pošalji knjigovođi
            </button>
            {showEmail && (
              <div className="absolute right-0 top-full mt-2 z-40 w-80 rounded-xl border border-white/10 bg-zinc-900 p-3 shadow-2xl"
                   data-testid="fuel-email-popover">
                <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">E-mail knjigovođe</p>
                <div className="flex items-center gap-2">
                  <input
                    type="email"
                    value={accEmail}
                    onChange={(e) => setAccEmail(e.target.value)}
                    placeholder="knjigovodja@ured.hr"
                    className="flex-1 bg-black/40 border border-white/10 px-3 py-2 rounded-lg text-sm focus:outline-none focus:border-[#C5A059]/60"
                    data-testid="fuel-accountant-email-input"
                  />
                  <button
                    onClick={sendToAccountant}
                    disabled={sending}
                    className="h-9 px-3 rounded-lg bg-[#C5A059] text-black hover:bg-[#D4AF37] disabled:opacity-50 transition"
                    data-testid="fuel-email-accountant-send"
                    aria-label="Pošalji"
                  >
                    {sending ? "…" : <Send className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-[10px] text-zinc-500 mt-2">Šalje manifest.xlsx (odobreni računi) kao privitak.</p>
              </div>
            )}
          </div>
          <CategorySettings />
          <button
            onClick={exportPdf}
            disabled={pdfBusy}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#C5A059]/40 text-[#C5A059] text-sm hover:bg-[#C5A059]/10 disabled:opacity-60 transition"
            title="PDF izvještaj — specifikacija troškova + slike svih računa"
            data-testid="fuel-export-pdf"
          >
            {pdfBusy
              ? <span className="w-4 h-4 rounded-full border-2 border-[#C5A059]/30 border-t-[#C5A059] animate-spin" />
              : <FileText className="w-4 h-4" />}
            PDF Izvještaj
          </button>
        </div>
      </div>

      <BulkQueuePanel drivers={driversList} />

      <MonthlyRefundsPanel />

      {/* Bulk accounting-name bar */}
      {selected.length > 0 && (
        <div data-testid="bulk-acct-bar"
             className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-[#C5A059]/30 bg-[#C5A059]/5 px-4 py-3">
          <span className="text-sm text-[#C5A059] font-semibold whitespace-nowrap">
            {selected.length} odabrano
          </span>
          <input
            list="acct-driver-names"
            value={bulkName}
            onChange={(e) => setBulkName(e.target.value)}
            placeholder="Ime za knjigovodstvo…"
            className="flex-1 min-w-[200px] max-w-xs bg-black/40 border border-white/10 px-3 py-1.5 rounded-lg text-sm focus:outline-none focus:border-[#C5A059]/60"
            data-testid="bulk-acct-input"
          />
          <button
            onClick={() => bulkName.trim() ? bulkAssign(bulkName.trim()) : toast.error("Unesite ime za knjigovodstvo.")}
            disabled={bulkBusy}
            className="px-3 py-1.5 rounded-full bg-[#C5A059] text-black text-sm font-semibold hover:bg-[#D4AF37] disabled:opacity-50 transition"
            data-testid="bulk-acct-apply"
          >
            {bulkBusy ? "Spremanje…" : "Primijeni"}
          </button>
          <button
            onClick={() => bulkAssign("")}
            disabled={bulkBusy}
            className="px-3 py-1.5 rounded-full border border-white/15 text-zinc-300 text-sm hover:bg-white/10 disabled:opacity-50 transition"
            title="Ukloni dodijeljeno ime — izvozi se vraćaju na originalnog vozača"
            data-testid="bulk-acct-clear"
          >
            Vrati na vozača
          </button>
          <button
            onClick={() => setSelected([])}
            className="ml-auto text-zinc-500 hover:text-white text-sm transition"
            data-testid="bulk-acct-cancel"
          >
            Poništi odabir
          </button>
        </div>
      )}
      <datalist id="acct-driver-names">
        {driverNames.map((n) => <option key={n} value={n} />)}
      </datalist>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.25em] text-zinc-500 border-b border-white/5">
              <th className="px-3 py-3 w-8">
                <input
                  type="checkbox"
                  checked={rows.length > 0 && selected.length === rows.length}
                  onChange={toggleSelectAll}
                  className="accent-[#C5A059] cursor-pointer"
                  data-testid="bulk-select-all"
                />
              </th>
              <th className="px-3 py-3">Date</th>
              <th className="px-3 py-3">Driver</th>
              <th className="px-3 py-3">Kategorija</th>
              <th className="px-3 py-3">Vehicle</th>
              <th className="px-3 py-3 text-right">Amount</th>
              <th className="px-3 py-3 text-right">Refund</th>
              <th className="px-3 py-3">Status</th>
              <th className="px-3 py-3">Photo</th>
              <th className="px-3 py-3 w-40">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={10} className="py-10 text-center text-zinc-500">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={10} className="py-10 text-center text-zinc-500">No receipts yet.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className={`border-b border-white/5 ${selected.includes(r.id) ? "bg-[#C5A059]/5" : ""}`} data-testid={`fuel-row-${r.id}`}>
                <td className="px-3 py-3">
                  <input
                    type="checkbox"
                    checked={selected.includes(r.id)}
                    onChange={() => toggleSelect(r.id)}
                    className="accent-[#C5A059] cursor-pointer"
                    data-testid={`bulk-select-${r.id}`}
                  />
                </td>
                <td className="px-3 py-3 text-zinc-300 whitespace-nowrap">{r.receipt_date || "—"}</td>
                <td className="px-3 py-3 text-white">
                  {r.driver_name}
                  {r.accounting_name && r.accounting_name !== r.driver_name && (
                    <span data-testid={`acct-name-${r.id}`} className="block text-[10px] text-[#C5A059] mt-0.5">
                      Knjig.: {r.accounting_name}
                    </span>
                  )}
                </td>
                <td className="px-3 py-3">
                  <select
                    value={r.category || "gorivo"}
                    onChange={(e) => changeCategory(r.id, e.target.value)}
                    className="bg-black/40 border border-white/10 rounded-lg px-2 py-1 text-xs text-zinc-200 focus:outline-none focus:border-[#C5A059]/60"
                    data-testid={`fuel-category-${r.id}`}
                  >
                    {CATEGORY_OPTIONS.map((c) => (
                      <option key={c.slug} value={c.slug}>{c.emoji} {c.label}</option>
                    ))}
                  </select>
                </td>
                <td className="px-3 py-3">
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${
                    r.vehicle_ownership === "own"
                      ? "border-[#C5A059]/40 text-[#C5A059]"
                      : "border-zinc-600 text-zinc-400"
                  }`}>
                    {r.vehicle_ownership === "own" ? "Own" : "Company"}
                  </span>
                </td>
                <td className="px-3 py-3 text-right text-zinc-200">{eur(r.amount_eur)}</td>
                <td className="px-3 py-3 text-right text-[#C5A059]">{eur(r.refund_eur)}</td>
                <td className="px-3 py-3">
                  <StatusPill status={r.status} />
                  {r.ocr?.needs_review && (
                    <span className="ml-1 text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full border border-amber-500/50 text-amber-400" title="OCR nesiguran — ručno provjerite iznos/JIR" data-testid={`fuel-needs-review-${r.id}`}>
                      ⚠ provjeri
                    </span>
                  )}
                </td>
                <td className="px-3 py-3">
                  <button
                    onClick={() => openPreview(r)}
                    className="text-zinc-400 hover:text-[#C5A059] transition inline-flex items-center gap-1 text-xs"
                    data-testid={`fuel-preview-${r.id}`}
                  >
                    <ImageIcon className="w-4 h-4" /> View
                  </button>
                </td>
                <td className="px-3 py-3">
                  <div className="flex items-center gap-1">
                    <IconBtn
                      onClick={() => setReceiptStatus(r.id, "approved")}
                      title="Approve"
                      testId={`approve-${r.id}`}
                      color="green"
                    ><Check className="w-3.5 h-3.5" /></IconBtn>
                    <IconBtn
                      onClick={() => setReceiptStatus(r.id, "rejected")}
                      title="Reject"
                      testId={`reject-${r.id}`}
                      color="red"
                    ><X className="w-3.5 h-3.5" /></IconBtn>
                    <IconBtn
                      onClick={() => deleteReceipt(r.id)}
                      title="Delete"
                      testId={`delete-${r.id}`}
                      color="zinc"
                    ><Trash2 className="w-3.5 h-3.5" /></IconBtn>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* OCR verification modal */}
      {preview && (
        <ReceiptVerifyModal
          receipt={preview}
          photoUrl={previewUrl}
          onClose={closePreview}
          onSaved={() => { closePreview(); load(); }}
        />
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const styles = {
    pending:  "border-amber-500/40 text-amber-400",
    approved: "border-emerald-500/40 text-emerald-400",
    rejected: "border-red-500/40 text-red-400",
  };
  return (
    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${styles[status] || "border-zinc-600 text-zinc-500"}`}>
      {status}
    </span>
  );
}

function IconBtn({ children, onClick, title, testId, color }) {
  const palette = {
    green: "border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10",
    red:   "border-red-500/30 text-red-400 hover:bg-red-500/10",
    zinc:  "border-white/10 text-zinc-400 hover:bg-white/10",
  }[color] || "border-white/10 text-zinc-400";
  return (
    <button
      onClick={onClick}
      title={title}
      data-testid={testId}
      className={`p-1.5 rounded-full border ${palette} transition`}
    >
      {children}
    </button>
  );
}

function CategorySettings() {
  const [open, setOpen] = useState(false);
  const [cats, setCats] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadCats = async () => {
    try {
      const { data } = await api.get("/admin/expense-categories");
      setCats(data.categories || []);
    } catch {
      toast.error("Postavke kategorija nije moguće učitati.");
    }
  };

  const toggle = () => {
    if (!open) loadCats();
    setOpen((v) => !v);
  };

  const save = async () => {
    setBusy(true);
    try {
      const pcts = Object.fromEntries(cats.map((c) => [c.slug, Number(c.pct)]));
      for (const [slug, p] of Object.entries(pcts)) {
        if (Number.isNaN(p) || p < 0 || p > 100) {
          setBusy(false);
          return toast.error(`Postotak za "${slug}" mora biti 0-100.`);
        }
      }
      await api.put("/admin/expense-categories", { pcts });
      toast.success("Zadani postoci kategorija spremljeni.");
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Spremanje nije uspjelo.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={toggle}
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 text-zinc-400 text-sm hover:border-[#C5A059] hover:text-[#C5A059] transition"
        data-testid="expense-categories-open"
        title="Zadani postoci povrata po kategoriji troška"
      >
        <Settings2 className="w-4 h-4" /> Kategorije troškova
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 z-40 w-80 rounded-xl border border-white/10 bg-zinc-900 p-3 shadow-2xl"
             data-testid="expense-categories-popover">
          <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Zadani % povrata po kategoriji</p>
          <div className="space-y-2">
            {cats.map((c, i) => (
              <div key={c.slug} className="flex items-center justify-between gap-2">
                <span className="text-xs text-zinc-300">{c.emoji} {c.label}</span>
                <div className="relative w-24">
                  <input
                    type="number" min="0" max="100" step="0.5"
                    value={c.pct}
                    onChange={(e) => {
                      const next = [...cats];
                      next[i] = { ...c, pct: e.target.value };
                      setCats(next);
                    }}
                    className="w-full bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 pr-6 text-xs text-right focus:outline-none focus:border-[#C5A059]/60"
                    data-testid={`category-pct-${c.slug}`}
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 text-[10px]">%</span>
                </div>
              </div>
            ))}
          </div>
          <button
            onClick={save}
            disabled={busy || cats.length === 0}
            className="mt-3 w-full h-9 rounded-lg bg-[#C5A059] text-black text-sm font-semibold hover:bg-[#D4AF37] disabled:opacity-50 transition"
            data-testid="expense-categories-save"
          >
            {busy ? "Spremanje…" : "Spremi"}
          </button>
          <p className="text-[10px] text-zinc-500 mt-2">Pojedinačni vozači mogu imati vlastite postotke u upravljanju vozačima.</p>
        </div>
      )}
    </div>
  );
}
